import { useTranslator } from '@mybridge/ui/custom-hooks/useTranslation';
import { useDisclosure, useToast } from '@mybridge/ui/hooks';
import { useInfiniteQuery, useMutation, useQuery } from '@tanstack/react-query';
import { useRouter } from 'next/router';
import { MatrixContext } from 'packages/mybridgev4/context/chat-context';
import { useChatSocket } from 'packages/mybridgev4/hooks/useChatSocket';
import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getUserFullName } from 'v4/lib/commons';
import { inviteUserToChat } from 'v4/store/actions/meeting.actions';
import {
  acceptDiskSubscriptionChangeRequest,
  cancelDiskSubscriptionChangeRequest,
  changeTeamDiskSubscriber,
  getDiskSubscriptionChangeRequests,
} from 'v4/store/actions/myDisk.Action';
import {
  approveGroupPost,
  createGourpPost,
  createTeamCommunity,
  deleteTeam,
  denyTeamJoinRequest,
  getGroupAdminPost,
  getGroupPost,
  getTeamCommunityList,
  getTeamDetailsBySlug,
  getTeamMemberList,
  getTeamOrganiserList,
  inviteUsersToTeam,
  joinTeamRequest,
  leaveTeamCommunity,
  removeTeamMember,
  reportTeamCommunity,
  sendApprovedMessageToGroup,
  updateDiskStatus,
  updateTeamDetails,
  updateTeamMemberStatus,
  withdrawTeamInvite,
} from 'v4/store/actions/teamCommunity.actions';
import { ANALYTICS_EVENTS, trackEvent } from 'v4/utils/analyticsEvents';
import { getNextPageFromURL } from 'v4/utils/utils';

export const TeamsContext = createContext({});

/**
 * Teams context hook
 * @param {*} param0
 * @returns
 */
export const useTeamsContext = ({
  slug,
  teamDetailsRefetchEnabled = false,
  teamMembersRefetchEnabled = false,
  teamsListingRefetchEnabled = false,
  teamOrganizersRefetchEnabled = false,
  teamJoinRequestsRefetchEnabled = false,
  teamPendingPostsRefetchEnabled = false,
  teamInvitedUsersRefetchEnabled = false,
  teamBlockedMembersRefetchEnabled = false,
  diskSubscriptionChangeRequestsRefetchEnabled = false,
}) => {
  const [mode, setMode] = useState('create'); // create | edit
  const [team, setTeam] = useState();
  const [text, setText] = useState('');
  const {onEvent} = useChatSocket();
  const [images, setImages] = useState([]);
  const [videos, setVideos] = useState([]);
  const [gifs, setGifs] = useState([]);
  const [expanded, setExpanded] = useState(false);
  const [mediaUpdated, setMediaUpdated] = useState(false);
  const [selectedTeam, setSelectedTeam] = useState();
  const [selectedUser, setSelectedUser] = useState();
  const [filter, setFilter] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const { back, push, pathname, query } = useRouter();
  const { allDegreeConnections = [], allDegreeConnectionsArr = [] } =
    useSelector((s) => s.connection) ?? {};
  const { t } = useTranslator();
  const { userProfileInfo } = useSelector((s) => s.userProfile) ?? {};
  const { loggedIn } = useSelector((s) => s.user) ?? {};
  const invitationDisc = useDisclosure();
  const createEditDisc = useDisclosure();
  const leaveTeamDisc = useDisclosure();
  const reportTeamDisc = useDisclosure();
  const removeMemberDisc = useDisclosure();
  const blockTeamMemberDisc = useDisclosure();
  const diskStatusDisc = useDisclosure();
  const makeOrganizerDisc = useDisclosure();
  const removeOrganizerDisc = useDisclosure();
  const deleteTeamDisc = useDisclosure();
  const changeDiskSubscriberDisc = useDisclosure();
  const diskSubscriptionChangeRequestDisc = useDisclosure();
  const [chatRoom, setChatRoom] = useState();
  const [diskStatus, setDiskStatus] = useState();
  const [activeTab, setActiveTab] = useState(t('myMessages.members') || 'Members');
  const myUserId = userProfileInfo?.matrix_user_id;
  const [defaultWelcomeNote, setDefaultWelcomeNote] = useState(null) 
  const [membersLoaded, setMembersLoaded] = useState(false);
  const [organizersLoaded, setOrganizersLoaded] = useState(false);
  const [chatTeamSlug, setChatTeamSlug] = useState(null);
  const audienceDisc = useDisclosure();
  const [permissionValue, setPermissionValue] = useState(null);
  const [currentPermissionValue, setCurrentPermissionValue] = useState(1);
  const [permissionId, setPermissionId] = useState(null);
  const [newTeamJoinRequests, setNewTeamJoinRequests] = useState([]);
  const [errorExistCompany,setErrorExistCompany]=useState(true);
 
  useEffect(() => {
    if (query?.activeTab === 'pending-posts') {
      setActiveTab(t('teamsPage.pendingPosts') || 'Pending Posts');
    }
  }, [query?.activeTab]);

  const diskAccessStatuses = useMemo(
    () => ({
      NO: {
        title: t('teamsPage.noDiskAccess') || 'No disk access',
        color: 'red.500',
      },
      R: {
        title: t('teamsPage.readAccess') || 'Read access',
        color: '',
      },
      W: {
        title: t('teamsPage.writeAccess') || 'Write access',
        color: 'green.500',
      },
    }),
    []
  );

  const toast = useToast({
    position: 'top',
    status: 'success',
  });
  const textInputRef = useRef();
  const dispatch = useDispatch();

  const { matrixClient, loggedInUser, chatPresets, setTeamsChats } =
    useContext(MatrixContext) ?? {};

  const addImage = (file) => {
    images.push(file);
    setMediaUpdated(true);
    setImages([...images]);
  };

  const removeImage = (file, index) => {
    const images_ = images?.filter((_, ind) => ind !== index);
    setMediaUpdated(true);
    setImages(images_);
  };

  const addGif = (file) => {
    gifs.push(file);
    setMediaUpdated(true);
    setGifs([...gifs]);
  };

  const removeGif = (file, index) => {
    const gifs_ = gifs?.filter((_, ind) => ind !== index);
    setGifs(gifs_);
  };

  const addVideo = (file) => {
    videos.push(file);
    setMediaUpdated(true);
    setVideos([...videos]);
  };

  const removeVideo = (file, index) => {
    const videos_ = videos?.filter((_, ind) => ind !== index);
    setMediaUpdated(true);
    setVideos(videos_);
  };

  const handleFileTypes = (file, type) => {
    if (type === 'file') {
      if (file.type.includes('image')) {
        return 'm.image';
      } else if (file.type.includes('application')) {
        return 'm.file';
      } else if (file.type.includes('audio')) {
        return 'm.audio';
      } else if (file.type.includes('video')) {
        return 'm.video';
      } else {
        return;
      }
    } else {
      if (file.includes('image') || file.includes('gif')) {
        return 'm.image';
      } else if (file.includes('application')) {
        return 'm.file';
      } else if (file.includes('audio')) {
        return 'm.audio';
      } else if (file.includes('video')) {
        return 'm.video';
      } else {
        return;
      }
    }
  };

  // teams listing
  const {
    data: teamsListingPages,
    isFetching: teamsListingFetching,
    isLoading: teamsListingLoading,
    refetch: refetchTeamsListing,
    fetchNextPage: teamsFetchNextPage,
    hasNextPage: teamsHasNextPage,
  } = useInfiniteQuery({
    queryKey: ['teams-listing', { searchQuery, filter }],
    queryFn: async ({ queryKey, pageParam }) => {
      const resp = (
        await dispatch(
          getTeamCommunityList({ pageParam, query: queryKey?.[1] })
        )
      )?.payload;
      return resp;
    },
    getNextPageParam: (lastPage) => getNextPageFromURL(lastPage?.next),
    refetchOnWindowFocus: false,
    enabled: teamsListingRefetchEnabled,
  });

  // create team
  const { mutateAsync: createTeamAsync, isLoading: createTeamLoading } =
    useMutation({
      mutationKey: ['create-team'],
      mutationFn: async (team) => {
        const resp = (await dispatch(createTeamCommunity(team)))?.payload;
        // console.log(resp, 'qwerty')
        // if (resp.id) {
        //   try {
        //     await matrixClient
        //       .createRoom({
        //         visibility: 'private',
        //         preset: chatPresets.PrivateChat,
        //         name: `${resp.name}`,
        //         room_alias_name: `${resp.slug}-team-group`,
        //         is_direct: false,
        //         joinRules: 'invite',
        //         //   initial_state: [
        //         //     {
        //         //         type: "m.room.encryption",
        //         //         state_key: "",
        //         //         content: {
        //         //             algorithm: "m.megolm.v1.aes-sha2"
        //         //         }
        //         //     }
        //         // ]
        //       })
        //       .then((response) => {

        //         setTeamsChats((prev) => [...prev, response.room_id]);
        //         if (
        //           resp?.image_logo &&
        //           !resp?.image_logo?.includes('logo-placeholder-image')
        //         ) {
        //           const content = {
        //             url: resp.image_logo,
        //           };
        //           // Send the new avatar URL as a state event
        //           matrixClient
        //             .sendStateEvent(response.room_id, 'm.room.avatar', content)
        //             .then(() => {

        //               roomChanged(true);
        //             })
        //             .catch((err) => {
        //               console.error('Failed to change room avatar:', err);
        //             });
        //         }

        //         dispatch(
        //           updateTeamDetails({
        //             id: resp?.id,
        //             matrix_chat_room_id: response?.room_id,
        //           })
        //         );
        //       })
        //       .catch((error) => {
        //         console.error('Error creating room:', error);
        //       });
        //   } catch (e) {
        //     console.error(e);
        //   }
        // }
        return resp;
      },
      onSuccess: ()=> {
        trackEvent(ANALYTICS_EVENTS.CreateTeam, userProfileInfo?.slug || 'anonymous');
      }
    });

  //update team
  const { mutateAsync: updateTeamAsync, isLoading: updateTeamLoading } =
    useMutation({
      mutationKey: ['update-team'],
      mutationFn: async (team) => {
        const resp = (await dispatch(updateTeamDetails(team)))?.payload;
        return resp;
      },
    });

  //leave team
  const { mutateAsync: leaveTeamAsync, isLoading: leaveTeamLoading } =
    useMutation({
      mutationKey: ['leave-team'],
      mutationFn: async (team) => {
        const resp = (
          await dispatch(
            leaveTeamCommunity({ for_team_community_id: team?.id })
          )
        )?.payload;
        return resp;
      },
    });

  //report team
  const { mutateAsync: reportTeamAsync, isLoading: reportTeamLoading } =
    useMutation({
      mutationKey: ['report-team'],
      mutationFn: async ({ team, message }) => {
        const resp = (
          await dispatch(
            reportTeamCommunity({
              team: team?.id,
              reported_by: userProfileInfo?.id,
              message: message,
            })
          )
        )?.payload;
        return resp;
      },
    });

  // update member status
  const {
    mutateAsync: updateTeamMemberStatusAsync,
    isLoading: updateTeamMemberStatusLoading,
  } = useMutation({
    mutationKey: ['update-team-member-status'],
    mutationFn: async (payload) => {
      const resp = (await dispatch(
        updateTeamMemberStatus({
          body: payload,
          throwErrorHandler: true,
          defaultStatusCode: true,
        })
      ))?.payload;
      
      return resp;
    },
  });

  // remove team member
  const {
    mutateAsync: removeTeamMemberAsync,
    isLoading: removeTeamMemberLoading,
  } = useMutation({
    mutationKey: ['remove-team-member'],
    mutationFn: async (payload) => {
      const resp = (await dispatch(removeTeamMember(payload)))?.payload;
      return resp;
    },
  });

  // update team member's disk access status
  const {
    mutateAsync: updateTeamMemberDiskStatusAsync,
    isLoading: updateTeamMemberDiskStatusLoading,
  } = useMutation({
    mutationKey: ['update-team-member-disk-status'],
    mutationFn: async (payload) => {
      const resp = (await dispatch(updateDiskStatus(payload)))?.payload;
      return resp;
    },
  });

  // join request
  const {
    mutateAsync: requestToJoinTeamAsync,
    isLoading: requestToJoinTeamLoading,
  } = useMutation({
    mutationKey: ['request-to-join-team'],
    mutationFn: async (payload) => {
      const resp = (await dispatch(joinTeamRequest(payload)))?.payload;
      return resp;
    },
  });

  // Deny join request
  const {
    mutateAsync: denyJoinRequestAsync,
    isLoading: denyJoinRequestLoading,
  } = useMutation({
    mutationKey: ['deny-join-request'],
    mutationFn: async (payload) => {
      const resp = (await dispatch(denyTeamJoinRequest(payload)))?.payload;
      return resp;
    },
  });

  // Delete team
  const { mutateAsync: deleteTeamAsync, isLoading: deleteTeamLoading } =
    useMutation({
      mutationKey: ['delete-team'],
      mutationFn: async (payload) => {
        const resp = (await dispatch(deleteTeam(payload)))?.payload;
        return resp;
      },
    });

  // invite user
  const {
    mutateAsync: inviteUserToTeamAsync,
    isLoading: inviteUserToTeamLoading,
  } = useMutation({
    mutationKey: ['invite-user-to-team'],
    mutationFn: async (payload) => {
      const resp = (await dispatch(inviteUsersToTeam(payload)))?.payload;
      return resp;
    },
    onSuccess: () => {
      trackEvent(ANALYTICS_EVENTS.TeamInvite, userProfileInfo?.slug || 'anonymous');
    }
  });

  // withdraw invite
  const {
    mutateAsync: withdrawTeamInviteAsync,
    isLoading: withdrawTeamInviteLoading,
  } = useMutation({
    mutationKey: ['withdraw-team-invite'],
    mutationFn: async (payload) => {
      const resp = (await dispatch(withdrawTeamInvite(payload)))?.payload;
      return resp;
    },
  });

  // ------ 
  // teams details
  const {
    data: teamDetails,
    isFetching: teamDetailsFetching,
    isLoading: teamDetailsLoading,
    refetch: refetchTeamDetails,
  } = useQuery(
    ['team-details', chatTeamSlug ? chatTeamSlug : slug],
    async ({ queryKey }) => {
      const resp = (await dispatch(getTeamDetailsBySlug(queryKey?.[1])))
        ?.payload?.[0];
      // if (resp?.id) {
      //   await setTeam(resp);
      //   // Removed refetchTeamOrganizers() and refetchTeamMembers() calls from here
      //   // to prevent infinite loops. These will be fetched separately when needed.
      //   try {
      //     if (resp?.matrix_chat_room_id) {
      //       const room = await matrixClient?.getRoom(resp?.matrix_chat_room_id);
      //       if (room) {
      //         // setChatRoom(room?.roomId);
      //       }
      //     } else if (loggedIn && matrixClient) {
      //       await matrixClient
      //         .createRoom({
      //           visibility: 'private',
      //           preset: chatPresets.PrivateChat,
      //           name: `${resp.name}-team-group`,
      //           room_alias_name: `${resp.slug}-team-group`,
      //           is_direct: false,
      //           joinRules: 'invite',
      //         })
      //         .then((response) => {

      //           dispatch(
      //             updateTeamDetails({
      //               id: resp?.id,
      //               matrix_chat_room_id: response?.room_id,
      //             })
      //           );
      //         })
      //         .catch((error) => {
      //           console.error('Error creating room:', error);
      //         });
      //     }
      //   } catch (e) {
      //     console.error(e);
      //   }
      // }
      if(resp?.id && resp?.organize_type==="community"){
        setDefaultWelcomeNote(t('teamCommunity.welcomeToCommunity') || 'Welcome to the community, [Name]');
      }else{
        setDefaultWelcomeNote(t('teams.welcomeToTeam') || 'Welcome to the team, [Name]');
      }

      return resp ?? {};
    },
    {
      refetchOnWindowFocus: false,
      enabled: teamDetailsRefetchEnabled && !!slug,
    }
  );

  // team members
  const {
    data: teamMembers,
    isFetching: teamMembersFetching,
    isLoading: teamMembersLoading,
    refetch: refetchTeamMembers,
  } = useQuery({
    queryKey: ['team-members', team?.id || teamDetails?.id],
    queryFn: async ({ queryKey }) => {
      if (!queryKey?.[1]) return [];
      const resp =
        (
          await dispatch(
            getTeamMemberList({
              for_team_community_id: queryKey?.[1],
              list_type: 'member',
            })
          )
        )?.payload?.results ?? [];
      setMembersLoaded(true);
      return resp;
    },
    refetchOnWindowFocus: false,
    enabled: !!(team?.id || teamDetails?.id),
  });

  // team members
  const {
    data: teamLoggedInMember,
    isFetching: teamLoggedInMembersFetching,
    isLoading: teamLoggedInMembersLoading,
    refetch: refetchTeamLoggedInMember,
  } = useQuery({
    queryKey: ['team-logged-in-member', team?.id || teamDetails?.id, userProfileInfo?.id],
    queryFn: async ({ queryKey }) => {
      if (!queryKey?.[1] || !queryKey?.[2]) return [];
      const resp =
        (
          await dispatch(
            getTeamMemberList({
              for_team_community_id: queryKey?.[1],
              list_type: 'member',
              user_id: queryKey?.[2],
            })
          )
        )?.payload?.results ?? [];
      setMembersLoaded(true);
      return resp;
    },
    refetchOnWindowFocus: false,
    enabled: !!(team?.id || teamDetails?.id) && !!userProfileInfo?.id && !!loggedIn,
  });

  // Join Requests
  const {
    data: teamJoinRequests,
    isFetching: teamJoinRequestsFetching,
    refetch: refetchTeamJoinRequests,
  } = useQuery({
    queryKey: ['team-join-requests', teamDetails?.id],
    queryFn: async ({ queryKey }) => {
      if (!queryKey?.[1]) return [];
      const resp =
        (
          await dispatch(
            getTeamMemberList({
              for_team_community_id: queryKey?.[1],
              list_type: 'requested',
            })
          )
        )?.payload?.results ?? [];
      return resp;
    },
    refetchOnWindowFocus: false,
    enabled: teamJoinRequestsRefetchEnabled && !!teamDetails?.id && !!loggedIn,
  });

  // Penindg Posts
  const {
    data: teamPendingPosts,
    isFetching: teamPendingPostsFetching,
    refetch: refetchTeamPendingPosts,
  } = useQuery({
    queryKey: ['team-pending-posts', teamDetails?.id],
    queryFn: async ({ queryKey }) => {
      if (!queryKey?.[1]) return [];
      let resp;
      if (isOrganizer) {
        resp =
          (await dispatch(getGroupAdminPost(queryKey?.[1])))?.payload ?? [];
      } else {
        resp = (await dispatch(getGroupPost(queryKey?.[1])))?.payload ?? [];
      }
      return resp;
    },
    refetchOnWindowFocus: false,
    enabled: teamPendingPostsRefetchEnabled && !!teamDetails?.id && !!loggedIn,
  });

  // Invited Users
  const {
    data: teamInvitedUsers,
    isFetching: teamInvitedUsersFetching,
    refetch: refetchTeamInvitedUsers,
  } = useQuery({
    queryKey: ['team-invited-users', teamDetails?.id],
    queryFn: async ({ queryKey }) => {
      if (!queryKey?.[1]) return [];
      const resp =
        (
          await dispatch(
            getTeamMemberList({
              for_team_community_id: queryKey?.[1],
              list_type: 'invited',
            })
          )
        )?.payload?.results ?? [];
      return resp;
    },
    refetchOnWindowFocus: false,
    enabled: teamInvitedUsersRefetchEnabled && !!teamDetails?.id && !!loggedIn,
  });

  // Blocked Members
  const {
    data: teamBlockedMembers,
    isFetching: teamBlockedMembersFetching,
    refetch: refetchTeamBlockedMembers,
  } = useQuery({
    queryKey: ['team-blocked-members', teamDetails?.id],
    queryFn: async ({ queryKey }) => {
      if (!queryKey?.[1]) return [];
      const resp =
        (
          await dispatch(
            getTeamMemberList({
              for_team_community_id: queryKey?.[1],
              list_type: 'blocked',
            })
          )
        )?.payload?.results ?? [];
      return resp;
    },
    refetchOnWindowFocus: false,
    enabled: teamBlockedMembersRefetchEnabled && !!teamDetails?.id && !!loggedIn,
  });
  const {
    data: teamOrganizers,
    isFetching: teamOrganizersFetching,
    refetch: refetchTeamOrganizers,
  } = useQuery({
    queryKey: ['team-organizers', team?.id || teamDetails?.id],
    queryFn: async ({ queryKey }) => {
      if (!queryKey?.[1]) return [];
      const resp =
        (
          await dispatch(
            getTeamOrganiserList({
              for_team_community_id: queryKey?.[1],
              list_type: 'organizer',
            })
          )
        )?.payload?.results ?? [];
      setOrganizersLoaded(true);
      return resp;
    },
    refetchOnWindowFocus: false,
    enabled: teamOrganizersRefetchEnabled && !!(team?.id || teamDetails?.id) && !!loggedIn,
  });

  //create team posts
  const {
    mutateAsync: createTeamPostAsync,
    isLoading: createTeamPostAsyncLoading,
  } = useMutation({
    mutationKey: ['create-team-post'],
    mutationFn: async (post) => {
      let payload = {};
      {
        post?.file
          ? (payload = {
              for_team_community_id: post?.id,
              message: post?.message,
              file: post?.file,
              message_category: post?.message_category,
              mention_users: post?.mention_users,
            })
          : (payload = {
              for_team_community_id: post?.id,
              message: post?.message,
              message_category: post?.message_category,
              mention_users: post?.mention_users,
            });
      }
      const resp = (await dispatch(createGourpPost(payload)))?.payload;
      return resp;
    },
  });

  // get subscription change requests
  const {
    data: diskSubscriptionChangeRequests,
    isFetching: diskSubscriptionChangeRequestsFetching,
    isLoading: diskSubscriptionChangeRequestsLoading,
    refetch: refetchDiskSubscriptionChangeRequests,
  } = useQuery({
    queryKey: ['disk-subscription-change-requests', teamDetails?.id],
    queryFn: async ({ queryKey }) => {
      const resp =
        (await dispatch(getDiskSubscriptionChangeRequests()))?.payload ?? [];
      return resp;
    },
    refetchOnWindowFocus: true,
    enabled: diskSubscriptionChangeRequestsRefetchEnabled,
  });

  //request for changing team disk subscriber
  const {
    mutateAsync: changeTeamDiskSubscriberAsync,
    isLoading: changeTeamDiskSubscriberLoading,
  } = useMutation({
    mutationKey: ['change-team-disk-subscriber'],
    mutationFn: async (payload) => {
      const resp = (await dispatch(changeTeamDiskSubscriber(payload)))?.payload;
      return resp;
    },
  });

  //cancel request for changing team disk subscriber
  const {
    mutateAsync: cancelDiskSubscriptionChangeRequestAsync,
    isLoading: cancelDiskSubscriptionChangeRequestLoading,
  } = useMutation({
    mutationKey: ['cancel-disk-subscription-change-request'],
    mutationFn: async (payload) => {
      const resp = (
        await dispatch(cancelDiskSubscriptionChangeRequest(payload))
      )?.payload;
      return resp;
    },
  });

  //cancel request for changing team disk subscriber
  const {
    mutateAsync: acceptDiskSubscriptionChangeRequestAsync,
    isLoading: acceptDiskSubscriptionChangeRequestLoading,
  } = useMutation({
    mutationKey: ['accept-disk-subscription-change-request'],
    mutationFn: async (payload) => {
      const resp = (
        await dispatch(acceptDiskSubscriptionChangeRequest(payload))
      )?.payload;
      return resp;
    },
  });

  const acceptDiskSubscriptionChangeRequest_ = async (request) => {
    try {
      const resp = await acceptDiskSubscriptionChangeRequestAsync({
        request_id: request?.id,
      });
      if (resp?.code === 'UPGRADE_PLAN') {
        toast({
          title: t('toast.upgradeDiskSubscription') || 'Upgrade Disk Subscription!',
          status: 'error',
          description:
            t('toast.basicDiskSubscription') || 'You are having basic disc subscription. Please upgrade plan to standard or professional.',
          duration: 10000,
          isClosable: true,
        });
      } else if (resp?.status_code >= 400) {
        toast({
          title: 'Error',
          status: 'error',
          description: t('toast.genericError') || `Something went wrong!`,
          isClosable: true,
        });
        refetchTeamOrganizers?.();
        refetchTeamDetails?.();
        diskSubscriptionChangeRequestDisc?.onClose();
        refetchDiskSubscriptionChangeRequests();
        changeDiskSubscriberDisc?.onClose();
      } else {
        toast({
          title: 'Success',
          description: t('toast.diskSubscriberChanged') || `Disk subscriber changed succssfully!`,
          isClosable: true,
        });
        refetchTeamOrganizers?.();
        refetchTeamDetails?.();
        diskSubscriptionChangeRequestDisc?.onClose();
        refetchDiskSubscriptionChangeRequests();
        changeDiskSubscriberDisc?.onClose();
      }
    } catch (e) {
      toast({
        title: 'Error',
        status: 'error',
        description: e?.message ?? (t('toast.specificError') || 'Some error occuured'),
      });
    }
  };

  const cancelDiskSubscriptionChangeRequest_ = async (request) => {
    try {
      // alert(request?.id);
      const resp = await cancelDiskSubscriptionChangeRequestAsync({
        request_id: request?.id,
      });
      if (resp?.status >= 400) {
        toast({
          title: 'Error',
          status: 'error',
          description: t('toast.invalidSubscriptionChange') || 'Invalid subscription change request',
        });
      } else {
        toast({
          title: 'Success',
          description: resp?.message || t('toast.withdrawnSubscriptionChange') || `Disk subscriber change request withdrawn succssfully!`,
        });
        refetchTeamOrganizers?.();
        refetchTeamDetails?.();
        refetchDiskSubscriptionChangeRequests?.();
        diskSubscriptionChangeRequestDisc?.onClose?.();
        changeDiskSubscriberDisc?.onClose?.();
      }
    } catch (e) {
      toast({
        title: 'Error',
        status: 'error',
        description: e?.message ?? (t('toast.specificError') || 'Some error occuured'),
      });
    }
  };

  // const createTeamChatPosts = async (post) => {
  //   try {
  //     const resp = await createTeamPostAsync(post);
  //     if (resp?.status >= 400) {
  //       toast({
  //         title: 'Error',
  //         status: 'error',
  //         description: t('toast.invalidTeamDetails') || 'Invalid team details provided',
  //       });
  //     } else {
  //       toast({
  //         title: 'Success',
  //         description: t('toast.postPendingApproval') || `Your post is pending your admin's approval`,
  //       });
  //     }
  //   } catch (e) {
  //     toast({
  //       title: 'Error',
  //       status: 'error',
  //       description: e?.message ?? (t('toast.specificError') || 'Some error occuured'),
  //     });
  //   }
  // };

  const createTeamChatPosts = async (post) => {
    try {
      const resp = await createTeamPostAsync(post);
      if (resp?.status >= 400) {
        toast({
          title: 'Error',
          status: 'error',
          description: t('toast.invalidTeamDetails') || 'Invalid team details provided',
        });
      } else if (!resp || resp?.error) {
        toast({
          title: 'Error',
          status: 'error',
          description: resp?.error?.message || t('toast.specificError') || 'Some error occurred',
        });
      } else {
        trackEvent(ANALYTICS_EVENTS.TeamPendingPost, userProfileInfo?.slug || 'anonymous');
        toast({
          title: 'Success',
          description: t('toast.postPendingApproval') || `Your post is pending your admin's approval`,
        });
      }
    } catch (e) {
      toast({
        title: 'Error',
        status: 'error',
        description: e?.message ?? (t('toast.specificError') || 'Some error occuured'),
      });
    }
  };

  const createTeamChatRoom = async ({ team, visibility } = {}) => {
    try {
      const rooms = matrixClient?.getRooms?.() ?? [];
      const exists = rooms?.find?.((r) => r?.name === team?.slug);
      // console.log('existing room', exists);
      if (!exists) {
        const room = await matrixClient?.createRoom?.({
          name: team?.slug,
          visibility,
        });

        setChatRoom(room);
      } else {
        if (exists?.roomId !== chatRoom?.roomId) {

          setChatRoom(exists);
        }
      }
    } catch (e) {
      console.error(e);
    }
  };

  const createOrUpdateTeam = async (team) => {
    try {
      let resp;
      if (team?.id) {
        if (typeof team.banner_image === 'string') {
          delete team.banner_image;
        }
        if (typeof team.image_logo === 'string') {
          delete team.image_logo;
        }
        resp = await updateTeamAsync(team);
      } else {
        resp = await createTeamAsync(team);
      }

      if (resp?.status >= 400) {
        toast({
          title: 'Error',
          status: 'error',
          description: t('toast.invalidTeamDetails') || 'Invalid team details provided',
        });
      } else {
        toast({
          title: 'Success',
          description: resp?.organize_type === 'community' 
          ? `${t('createTeam.community') || 'Community'} ${team?.id ? (t('jobs.updatedSuccessfully') || 'updated successfully!') : `${t('teams.created') || "created"} ${t('toast.successGeneric') || "successfully"}`}`
          : `${t('teams.team') || "Team"} ${team?.id ? (t('jobs.updatedSuccessfully') || 'updated successfully!') : `${t('teams.created') || "created"} ${t('toast.successGeneric') || "successfully"}`}`,
        });
        if (team?.id) {
          refetchTeamDetails();
        } else {
          refetchTeamsListing();
        }
        createEditDisc?.onClose?.();
        return resp;
      }
    } catch (e) {
      toast({
        title: 'Error',
        status: 'error',
        description: e?.message ?? (t('toast.specificError') || 'Some error occuured'),
      });
    } finally {
    }
  };

  const leaveTeam = async (team, redirectToListing = false) => {
    try {
      const resp = await leaveTeamAsync(team);
      if (resp?.status >= 400) {
        toast({
          title: 'Error',
          status: 'error',
          description: t('toast.invalidTeamDetails') || 'Invalid team details provided',
        });
      } else {
        toast({
          title: 'Success',
          description: t('toast.leftTeam') || `Left team successfully`,
        });
        // matrixClient?.leave(team?.matrix_chat_room_id);
        if (redirectToListing) {
          back?.();
        } else {
          refetchTeamsListing();
          if(pathname.includes('/teams/') || pathname.includes('/t/')) {
            refetchTeamMembers?.();
            refetchTeamOrganizers?.();
          }
        }
        leaveTeamDisc?.onClose?.();
      }
    } catch (e) {
      toast({
        title: 'Error',
        status: 'error',
        description: e?.message ?? (t('toast.specificError') || 'Some error occuured'),
      });
    } finally {
    }
  };

  const reportTeam = async (
    team,
  ) => {
    try {

      const resp = await reportTeamAsync(team?.payload);
      if (resp?.status >= 400) {
        setErrorExistCompany(true)
        toast({
          title: 'Error',
          status: 'error',
          description: t('toast.invalidTeamDetails') || 'Invalid team details provided',
        });
      } else {
        setErrorExistCompany(false)
        if (team?.onReport) return
          toast({
            title: 'Success',
            description: t('toast.teamReported') || `Team reported successfully`,
          });
          if (team?.redirectToListing) {
            back?.();
          } else {
            refetchTeamsListing();
          }
          reportTeamDisc?.onClose?.();
        }
    } catch (e) {
      setErrorExistCompany(true)
      toast({
        title: 'Error',
        status: 'error',
        description: (t('toast.specificError') || 'Some error occuured'),
      });
    } finally {
    }
  };

  const requestToJoinTeam_ = async (team) => {
    // console.log(team);
    try {
      const resp = await requestToJoinTeamAsync({
        for_team_community_id: team?.id,
      });

      if (resp?.status >= 400) {
        toast({
          title: 'Error',
          status: 'error',
          description: t('toast.invalidTeamDetails') || 'Invalid team details provided',
        });
      } else {
        toast({
          title: t('toast.requestSent') || 'Request sent',
          description: t('searchPage.pleaseWaitForOrganizers') || `Please wait for the organizers to accept your request.`,
        });
        setNewTeamJoinRequests([...newTeamJoinRequests, teamJoinRequests]);
        refetchTeamJoinRequests?.();
        
      }
    } catch (e) {
      toast({
        title: 'Error',
        status: 'error',
        description: e?.message ?? (t('toast.specificError') || 'Some error occuured'),
      });
    } finally {
    }
  };

  const withdrawRequestToJoinTeam_ = async (member) => {
    try {
      const resp = await denyJoinRequestAsync({
        for_team_community_id: teamDetails?.id || selectedTeam?.id,
        user_id: member?.id,
      });
      if (resp?.status >= 400) {
        toast({
          title: 'Error',
          status: 'error',
          description: t('toast.invalidTeamDetails') || 'Invalid team details provided',
        });
      } else {
        toast({
          title: t('toast.reqWithdrawn') || 'Request withdrawn',
          description: t('searchPage.requestWithdrawn') || `Request to join this team has been withdrawn.`,
        });
        setNewTeamJoinRequests([...newTeamJoinRequests, teamJoinRequests]);
        refetchTeamJoinRequests?.();
      }
    } catch (e) {
      toast({
        title: 'Error',
        status: 'error',
        description: e?.message ?? (t('toast.specificError') || 'Some error occuured'),
      });
    } finally {
    }
  };

  const approveTeamJoinRequest_ = async (user) => {
    let welcomeMessage = '';
    const name = `${user.first_name} ${user.last_name}`;
    const regex = /\[Name\]/;
    // if (teamDetails?.welcome_note?.length === 0) {
    //   welcomeMessage = defaultWelcomeNote.replace(regex, `${name}`);
    // } else {
    //   welcomeMessage = teamDetails?.welcome_note.replace(regex, `${name}`);
    // }
    if (!teamDetails?.welcome_note || teamDetails.welcome_note.length === 0) {
      const fallbackWelcomeNote = defaultWelcomeNote || (teamDetails?.organize_type === 'community' ? (t('teamCommunity.welcomeToCommunity') || 'Welcome to the community, [Name]') : (t('teams.welcomeToTeam') || 'Welcome to the team, [Name]'));
      welcomeMessage = fallbackWelcomeNote.replace(regex, `${name}`);
    } else {
      welcomeMessage = teamDetails.welcome_note.replace(regex, `${name}`);
    }
    // console.log('welcomeMessage', welcomeMessage);
    try {
      const resp = await updateTeamMemberStatusAsync({
        for_team_community_id: teamDetails?.id,
        user_id: user?.id,
        user_status: 'member',
      });

      if (resp?.status_code >= 400) {
        toast({
          title: 'Error',
          status: 'error',
          description: t('toast.invalidTeamDetails') || 'Invalid team details provided',
        });
      } else {
        toast({
          title: t('toast.requestAccepted') || 'Request accepted',
          description: t('toast.joinRequestApproved') || `Team join request has been approved.`,
        });
      }
      refetchTeamJoinRequests?.();
      refetchTeamMembers?.();
      await matrixClient
        ?.invite(
          teamDetails?.room_id,
          user?.user_id,
          'team_group_invite'
        )
        .then((response) => {
          if (response) {
            dispatch(
              inviteUserToChat({
                invitee_id: [user?.id],
                sender_id: userProfileInfo?.id,
                room_id: teamDetails?.room_id,
              })
            ).then((res) => {
              if (res && teamDetails?.is_enable_welcome_note) {
                const content = {
                  body: welcomeMessage,
                  msgtype: 'm.text',
                };
                matrixClient?.sendEvent(
                  teamDetails?.room_id,
                  'm.room.message',
                  content
                );
              }
            });
          }
        })
        .catch(console.error);
    } catch (e) {
      toast({
        title: 'Error',
        status: 'error',
        description: e?.message ?? (t('toast.specificError') || 'Some error occuured'),
      });
    } finally {
    }
  };

  // const approveTeamGroupPost_ = async (id, state, type) => {
  //   try {
  //     const resp = await dispatch(
  //       approveGroupPost({ id: id, status: state })
  //     ).then((res) => {
  //       if (res && state === 'accept') {
  //         let content = {};
  //         if (type === 'text') {
  //           content = {
  //             body: res?.payload?.message,
  //             msg_type: 'm.text',
  //           };
  //         } else {
  //           content = {
  //             msg_type: handleFileTypes(
  //               res.payload.message_category,
  //               'category'
  //             ),
  //             body: 'Image name',
  //             url: type === 'gif' ? res?.payload?.message : res?.payload?.file,
  //             media_url:
  //               type === 'gif' ? res?.payload?.message : res?.payload?.file,
  //           };
  //         }
  //         const body = {
  //           roomId: teamDetails.room_id,
  //           eventType: 'm.room.message',
  //           userId: res.payload.user,
  //           content: content,
  //         };
  //         dispatch(sendApprovedMessageToGroup(body))
  //       }
  //     });
  //     if (resp?.status >= 400) {
  //       toast({
  //         title: 'Error',
  //         status: 'error',
  //         description: (t('toast.specificError') || 'Some error occured!'),
  //       });
  //     } else {
  //       if(state === 'declined'){
  //         toast({
  //           description: 'Pending post declined successfully',
  //         })
  //       }
  //       else{ 
  //         toast({
  //           title: t('toast.requestAccepted') || 'Request accepted',
  //           description: 'Pending chat accepted successfully.' || t('toast.teamPostUpdated') || `Team post updated successfully`,
  //         });
  //       }
  //       refetchTeamPendingPosts?.();
  //     }
  //   } catch (e) {
  //     toast({
  //       title: 'Error',
  //       status: 'error',
  //       description: e?.message ?? (t('toast.specificError') || 'Some error occuured'),
  //     });
  //   } finally {
  //   }
  // };

  const approveTeamGroupPost_ = async (id, state, type) => {
    try {
        const resp = await dispatch(
          approveGroupPost({ id: id, status: state })
        );
        
        if (resp.type.includes('rejected')) {
          toast({
            title: 'Error',
            status: 'error',
            description: t('toast.specificError') || 'Some error occured!',
          });
          return;
        }

        if (state === 'declined') {
          toast({
            description: 'Pending post declined successfully',
          });
        } else {
          toast({
            title: t('toast.requestAccepted') || 'Request accepted',
            description:
              'Pending chat accepted successfully.' ||
              t('toast.teamPostUpdated') ||
              `Team post updated successfully`,
          });
        }

      refetchTeamPendingPosts?.();
    } catch (e) {
      toast({
        title: 'Error',
        status: 'error',
        description:
          e?.message ?? (t('toast.specificError') || 'Some error occurred'),
      });
    }
  };

  const denyTeamJoinRequest_ = async (member) => {
    try {
      const resp = await denyJoinRequestAsync({
        for_team_community_id: teamDetails?.id,
        user_id: member?.id,
      });
      if (resp?.status >= 400) {
        toast({
          title: 'Error',
          status: 'error',
          description: t('toast.invalidTeamDetails') || 'Invalid team details provided',
        });
      } else {
        toast({
          title: t('toast.requestDenied') || 'Request denied',
          description: t('toast.joinRequestDenied') || `Team join request has been denied.`,
        });

        refetchTeamJoinRequests?.();
      }
    } catch (e) {
      toast({
        title: 'Error',
        status: 'error',
        description: e?.message ?? (t('toast.specificError') || 'Some error occuured'),
      });
    } finally {
    }
  };

  const deleteTeam_ = async (team) => {
    try {
      const resp = await deleteTeamAsync(team?.id);
      if (resp?.status_code >= 400) {
        toast({
          title: 'Error',
          status: 'error',
          description: t('toast.deleteTeamError') || 'Could not delete team, try again later!',
        });
        return false;
      } else {
        toast({
          title: t('toast.teamDeleted') || 'Team deleted',
          description: t('toast.teamDeletedSuccess') || `Team deleted successfully.`,
        });
        return true;
      }
    } catch (e) {
      toast({
        title: 'Error',
        status: 'error',
        description: e?.message ?? (t('toast.specificError') || 'Some error occuured'),
      });
    } finally {
    }
  };

  const withdrawTeamInvite_ = async (member) => {
    try {
      const resp = await withdrawTeamInviteAsync({
        for_team_community_id: teamDetails?.id,
        user_id: member?.id,
      });
      if (resp?.status >= 400) {
        toast({
          title: 'Error',
          status: 'error',
          description: t('toast.invalidTeamDetails') || 'Invalid team details provided',
        });
      } else {
        toast({
          title: t('toast.inviteWithdrawn') || 'Invite withdrawn',
          description: t('toast.inviteWithdrawnSuccess') || `Invite to join team has been withdrawn.`,
        });

        refetchTeamInvitedUsers?.();
      }
    } catch (e) {
      toast({
        title: 'Error',
        status: 'error',
        description: e?.message ?? (t('toast.specificError') || 'Some error occuured'),
      });
    } finally {
    }
  };

  const blockTeamMember_ = async (member) => {
    try {
      const resp = await updateTeamMemberStatusAsync({
        for_team_community_id: teamDetails?.id,
        user_id: member?.id,
        user_status: 'blocked',
      });

      if (resp?.status >= 400) {
        toast({
          title: 'Error',
          status: 'error',
          description: t('toast.invalidTeamDetails') || 'Invalid team details provided',
        });
      } else {
        toast({
          title: t('toast.userBlock') || 'User blocked',
          description: t('toast.userBlockedSuccess') || `User has been blocked successfully.`,
        });

        trackEvent(ANALYTICS_EVENTS.TeamBlockedUsers, userProfileInfo?.slug || 'anonymous');

        refetchTeamBlockedMembers?.();
        refetchTeamMembers?.();
        blockTeamMemberDisc?.onClose?.();
        // try {
        //   await matrixClient.kick(
        //     teamDetails?.matrix_chat_room_id,
        //     member?.user?.matrix_user_id,
        //     ''
        //   );
        // } catch (e) {
        //   console.error(e);
        // }
      }
    } catch (e) {
      toast({
        title: 'Error',
        status: 'error',
        description: e?.message ?? (t('toast.specificError') || 'Some error occuured'),
      });
    } finally {
    }
  };

  const acceptInvite = async (member, team=teamDetails, route = false) => {
    let welcomeMessage = '';
    const name = `${member.first_name} ${member.last_name}`;
    const regex = /\[Name\]/;
    // if (teamDetails?.welcome_note?.length === 0) {
    //   welcomeMessage = defaultWelcomeNote.replace(regex, `${name}`);
    // } else {
    //   welcomeMessage = teamDetails?.welcome_note?.replace(regex, `${name}`);
    // }
    if (!teamDetails?.welcome_note || teamDetails.welcome_note.length === 0) {
      const fallbackWelcomeNote = defaultWelcomeNote || (teamDetails?.organize_type === 'community' ? (t('teamCommunity.welcomeToCommunity') || 'Welcome to the community, [Name]') : (t('teams.welcomeToTeam') || 'Welcome to the team, [Name]'));
      welcomeMessage = fallbackWelcomeNote.replace(regex, `${name}`);
    } else {
      welcomeMessage = teamDetails.welcome_note.replace(regex, `${name}`);
    }
    try {
      const resp = await updateTeamMemberStatusAsync({
        for_team_community_id: teamDetails?.id || team?.id,
        user_id: member?.id,
        user_status: 'member',
      });
      try {
        // matrixClient.joinRoom(member.roomId);
      
            setTeamsChats((prev) => [
              ...prev,
              teamDetails?.room_id,
            ]);
            // const content = {
            //   body: welcomeMessage,
            //   msg_type: 'm.text',
            // };
            // const body = {
            //   roomId: teamDetails.room_id,
            //   eventType: 'm.room.message',
            //   userId: teamDetails?.organizers[0]?.id,
            //   content: content,
            // };
            // if (teamDetails?.is_enable_welcome_note) {
            //   dispatch(sendApprovedMessageToGroup(body)).then((res) => {

            //   });
            // }
        
      } catch (e) {
        console.error('Team chat accept error', e);
        return;
      }
      // matrixClient.on('RoomMember.membership', function (event, member) {
      //   const content = event.getContent();
      //   if (
      //     member.membership === 'invite' &&
      //     member.userId === myUserId
      //      && content?.reason === 'team_group_invite' &&
      //     member.roomId === teamDetails?.matrix_chat_room_id
      //   ) {
      //     console.log('I am here');

      //   }
      // });
      if (resp?.status >= 400) {
        toast({
          title: 'Error',
          status: 'error',
          description: t('toast.invalidTeamDetails') || 'Invalid team details provided',
        });
      } else {
        toast({
          title: t('toast.inviteAccepted') || 'Invite accepted',
          description: t('toast.joinedTeam') || `You have become part of this team now.`,
        });
        
        setTimeout(()=>{
          push('/teams/' + teamDetails?.slug);
        }, 1000)
      }
    } catch (e) {
      toast({
        title: 'Error',
        status: 'error',
        description: e?.message ?? (t('toast.specificError') || 'Some error occuured'),
      });
    } finally {
      refetchTeamDetails().then(()=>{
        if(route) push(`/teams/${team?.slug}`);
      })
    }
  };

  const unblockTeamMember_ = async (member) => {
    try {
      const resp = await updateTeamMemberStatusAsync({
        for_team_community_id: teamDetails?.id,
        user_id: member?.id,
        user_status: 'member',
      });
      if (resp?.status >= 400) {
        toast({
          title: 'Error',
          status: 'error',
          description: t('toast.invalidTeamDetails') || 'Invalid team details provided',
        });
      } else {
        toast({
          title: t('toast.memberUnblocked') || 'Member unblocked',
          description: t('toast.memberUnblockedSuccess') || `Team member was unblocked successfully.`,
        });
        refetchTeamBlockedMembers?.();
        refetchTeamMembers?.();
      }
    } catch (e) {
      toast({
        title: 'Error',
        status: 'error',
        description: e?.message ?? (t('toast.specificError') || 'Some error occuured'),
      });
    } finally {
    }
  };

  const makeTeamOrganizer = async (member) => {
    try {
      const resp = await updateTeamMemberStatusAsync({
        for_team_community_id: teamDetails?.id,
        user_id: member?.id,
        user_status: 'organizer',
      });

      if (resp?.status >= 400) {
        toast({
          title: 'Error',
          status: 'error',
          description: t('toast.invalidTeamDetails') || 'Invalid team details provided',
        });
      } else {
        toast({
          title: 'Success',
          description: t('toast.madeOrganizer') || `Team member has been made organizer successfully.`,
        });
        refetchTeamMembers?.();
        refetchTeamOrganizers?.();
        makeOrganizerDisc?.onClose?.();
        try {
          await matrixClient.setPowerLevel(
            teamDetails?.matrix_chat_room_id,
            member?.user?.matrix_user_id,
            100
          );
        } catch (e) {
          console.error('Failed to change user admin:', e);
        }
      }
    } catch (e) {
      toast({
        title: 'Error',
        status: 'error',
        description: e?.message ?? (t('toast.specificError') || 'Some error occuured'),
      });
    } finally {
    }
  };

  const removeTeamOrganizer = async (member) => {
    try {
      const resp = await updateTeamMemberStatusAsync({
        for_team_community_id: teamDetails?.id,
        user_id: member?.id,
        user_status: 'member',
      });

      if (resp?.status >= 400) {
        toast({
          title: 'Error',
          status: 'error',
          description: t('toast.invalidTeamDetails') || 'Invalid team details provided',
        });
      } else {
        toast({
          title: 'Success',
          description: t('toast.removedOrganizer') || `Team member has been removed as an organizer successfully.`,
        });
        refetchTeamMembers?.();
        refetchTeamOrganizers?.();
        removeOrganizerDisc?.onClose?.();
        try {
          await matrixClient.setPowerLevel(
            teamDetails?.matrix_chat_room_id,
            member?.user?.matrix_user_id,
            0
          );
        } catch (e) {
          console.error(e);
        }
      }
    } catch (e) {
      toast({
        title: 'Error',
        status: 'error',
        description: e?.message ?? (t('toast.specificError') || 'Some error occuured'),
      });
    } finally {
    }
  };

  // const inviteUsersToTeam_ = async (member) => {
  //   const invitedMatrix = _.map(member, 'matrix_user_id');
  //   const invitedTeam = _.map(member, 'id');
  //   try {
  //     const resp = await inviteUserToTeamAsync({
  //       for_team_community_id: teamDetails?.id,
  //       invite_user_ids: invitedTeam,
  //     });
  //     invitedMatrix.forEach((member) => {
  //       matrixClient.invite(
  //         teamDetails?.matrix_chat_room_id,
  //         member,
  //         'team_group_invite'
  //       );
  //     });

  //     if (resp?.status >= 400) {
  //       toast({
  //         title: 'Error',
  //         status: 'error',
  //         description: t('toast.invalidTeamDetails') || 'Invalid team details provided',
  //       });
  //     } else {
  //       toast({
  //         title: t('toast.inviteSent') || 'Invite sent',
  //         description: t('toast.inviteSentSuccess') || `Invite to selected users has been sent successfully.`,
  //       });

  //       refetchTeamMembers?.();
  //       refetchTeamInvitedUsers?.();
  //       invitationDisc?.onClose?.();
  //       return resp;
  //     }
  //   } catch (e) {
  //     toast({
  //       title: t('toast.inviteSent') || 'Invite sent',
  //       description: t('toast.inviteSentSuccess') || `Invite to selected users has been sent successfully.`,
  //     });
  //   } finally {
  //   }
  // };

  const inviteUsersToTeam_ = async (member) => {
    const invitedMatrix = _.map(member, 'matrix_user_id');
    const invitedTeam = _.map(member, 'id');

    try {
      const resp = await inviteUserToTeamAsync({
        for_team_community_id: teamDetails?.id,
        invite_user_ids: invitedTeam,
      });

      // try {
      //   invitedMatrix.forEach((member, index) => {
      //     matrixClient.invite(
      //       teamDetails?.matrix_chat_room_id,
      //       member,
      //       'team_group_invite'
      //     );
      //   });
      // } catch (matrixError) {
      //   console.error('💬 Matrix invitation failed:', matrixError);
      // }

      // Check for errors - API response
      const hasError = resp?.status >= 400 || resp?.status_code >= 400;
      
      if (hasError) {
        toast({
          title: 'Error',
          status: 'error',
          description: t('toast.invalidTeamDetails') || 'Invalid team details provided',
        });
      } else {        
        toast({
          title: t('toast.inviteSent') || 'Invite sent',
          duration: 2000,
          description: t('toast.inviteSentSuccess') || `Invite to selected users has been sent successfully.`,
        });

        // Refetch data
        try {
          refetchTeamMembers?.();
          refetchTeamInvitedUsers?.();
          invitationDisc?.onClose?.();
        } catch (refetchError) {
          console.error('Refetch failed:', refetchError);
        }
        return resp;
      }
    } catch (e) {      
      toast({
        title: 'Error',
        status: 'error',
        description: e?.message ?? (t('toast.specificError') || 'Some error occurred'),
      });
    }
  };


  const removeTeamMember_ = async (member) => {
    try {
      const resp = await removeTeamMemberAsync({
        for_team_community_id: teamDetails?.id,
        user_id: member?.id,
        is_remove: true
      });
      // try {
      //   await matrixClient.kick(
      //     teamDetails?.matrix_chat_room_id,
      //     member?.matrix_user_id
      //   );
      // } catch (e) {
      //   console.error(e);
      // }

      if (resp?.status >= 400) {
        toast({
          title: 'Error',
          status: 'error',
          description: t('toast.invalidTeamDetails') || 'Invalid team details provided',
        });
      } else {
        toast({
          title: t('toast.memberRemoved') || 'Removed',
          description: t('toast.memberRemovedSuccess') || `Team member has been removed successfully.`,
        });

        refetchTeamMembers?.();
        removeMemberDisc?.onClose?.();
      }
    } catch (e) {
      toast({
        title: 'Error',
        status: 'error',
        description: e?.message ?? (t('toast.specificError') || 'Some error occuured'),
      });
    } finally {
    }
  };

  const updateTeamMemberDiskStatus_ = async (member, status) => {
    try {
      const resp = await updateTeamMemberDiskStatusAsync({
        for_team_community_id: teamDetails?.id,
        user_id: member?.id,
        disk_status: status,
      });
      if (resp?.status >= 400) {
        toast({
          title: 'Error',
          status: 'error',
          description: t('toast.invalidTeamDetails') || 'Invalid team details provided',
        });
      } else {
        toast({
          title: t('toast.diskAccessUpdated') || 'Disk access status updated',
          description: t('toast.diskAccessUpdatedSuccess') || `Disk access level has been updated successfully.`,
        });

        refetchTeamMembers?.();
        diskStatusDisc?.onClose?.();
      }
    } catch (e) {
      toast({
        title: 'Error',
        status: 'error',
        description: e?.message ?? (t('toast.specificError') || 'Some error occuured'),
      });
    } finally {
    }
  };

  const changeTeamDiskSubscriber_ = async (user) => {
    try {
      const userId = user?.user?.id || user?.id;
      const resp = await changeTeamDiskSubscriberAsync({
        team_id: teamDetails?.id,
        requested_to: userId,
      });
      if (resp?.status >= 400) {
        toast({
          title: 'Error',
          status: 'error',
          description: t('toast.invalidRequest') || 'Invalid request',
        });
      } else {
        if (resp?.message?.includes?.('already sent')) {
          return resp;
        } else {
          toast({
            title: t('toast.requestSentSuccess') || 'Request sent successfully!',
            description: `${(t('toast.subscriptionChangeSent') || 'Disk subscription change request has been sent to')} ${getUserFullName(user?.user)}`,
          });
        }

        refetchTeamOrganizers?.();
        refetchTeamDetails?.();
        changeDiskSubscriberDisc?.onClose?.();
      }
    } catch (e) {
      toast({
        title: 'Error',
        status: 'error',
        description: e?.message ?? (t('toast.specificError') || 'Some error occuured'),
      });
    } finally {
    }
  };

  const teamsListing = useMemo(() => {
    return [].concat.apply(
      [],
      teamsListingPages?.pages?.map((p) => p?.results ?? [])
    );
  }, [teamsListingPages]);

  const isOrganizer = useMemo(() => {
    return teamOrganizers?.find?.((tm) => tm?.user?.id === userProfileInfo?.id);
  }, [userProfileInfo, teamOrganizers]);

  const isMember = useMemo(() => {
    return (
      isOrganizer ||
      teamMembers?.find?.((tm) => tm?.user?.id === userProfileInfo?.id)
    );
  }, [userProfileInfo, teamMembers, isOrganizer]);

  const isInvited = useMemo(() => {
    return teamInvitedUsers?.find?.(
      (tm) => tm?.user?.id === userProfileInfo?.id
    );
  }, [userProfileInfo, teamInvitedUsers]);

  const isRequested = useMemo(() => {
    return teamJoinRequests?.find?.(
      (tm) => tm?.user?.id === userProfileInfo?.id
    );
  }, [userProfileInfo, teamJoinRequests]);

  const currentDiskAccess = useMemo(() => {
    const members = [...(teamMembers ?? []), ...(teamOrganizers ?? [])];
    const me = members?.find?.((tm) => tm?.user?.id === userProfileInfo?.id);
    return me?.disk_status;
  }, [userProfileInfo, teamMembers, teamOrganizers]);

  const isUserOrganizer = (member) => {
    return teamOrganizers?.find?.((tm) => tm?.user?.id === member?.id);
  };

  const isUserMember = (member) => {
    return teamMembers?.find?.((tm) => tm?.user?.id === member?.id);
  };

  const isUserInvited = (member) => {
    return teamInvitedUsers?.find?.((tm) => tm?.user?.id === member?.id);
  };

  const isUserRequested = (member) => {
    return teamJoinRequests?.find?.((tm) => tm?.user?.id === member?.id);
  };

  useEffect(() => {
    if (!teamLoggedInMember && teamDetails && loggedIn) {
      refetchTeamLoggedInMember();
    }
  }, [teamDetails, teamLoggedInMember, loggedIn]);


  useEffect(() => {
    if (!teamDetails?.id) return;

    const unsub = onEvent("team.member.accepted", (data)=>{
      if(data?.team_id === teamDetails?.id?.toString()){
        refetchTeamMembers()
      }
    });

    return () => unsub();
  }, [onEvent, teamDetails?.id, refetchTeamMembers])

  useEffect(() => {
    if (!teamDetails?.id) return;

    const unsub = onEvent("team.info.updated", (data) => {
      if (
        data?.team_id === teamDetails?.id ||
        data?.team_id === teamDetails?.id?.toString() ||
        data?.id === teamDetails?.id ||
        data?.id === teamDetails?.id?.toString()
      ) {
        refetchTeamDetails();
        refetchTeamLoggedInMember();
      }
    });

    return () => unsub();
  }, [onEvent, teamDetails?.id, refetchTeamDetails, refetchTeamLoggedInMember])



  const myDiskAccessType = useMemo(() => {
    if (teamLoggedInMember && teamDetails) {
      // return teamLoggedInMember[0]?.disk_status;
      return [...(teamOrganizers ?? []), ...(teamLoggedInMember ?? [])]?.find?.(
        (tm) => tm?.user?.id === userProfileInfo?.id
      )?.disk_status;
    } 
    // else {
    //   refetchTeamLoggedInMember();
    // }
    return null;
  }, [teamMembers, teamOrganizers, teamLoggedInMember, teamDetails]);

  // useEffect(() => {
  //   console.warn(matrixClient);
  //   console.warn(matrixClient?.getRooms?.());
  // }, [matrixClient]);
  // useEffect(() => {
  //   // console.log(matrixClient?.getRooms?.());
  //   if (matrixClient && loggedInUser && teamDetails?.matrix_chat_room_id) {
  //     const room = matrixClient?.getRoom(teamDetails?.matrix_chat_room_id);
  //     if (room) {
  //       setChatRoom(room);
  //     }
  //   }
  // }, [matrixClient, loggedInUser, teamDetails]);

  // useEffect(() => {
  //   refetchTeamsListing();
  // }, [searchQuery, filter]);

  useEffect(() => {
    // Only refetch if chatTeamSlug changed and is different from current slug
    // This prevents infinite loops when chatTeamSlug is set to the same value
    if (chatTeamSlug && loggedIn && chatTeamSlug !== slug && !teamDetailsFetching) {
      refetchTeamDetails();
      refetchDiskSubscriptionChangeRequests();
    }
  }, [chatTeamSlug, loggedIn, slug]);

  useEffect(() => {
    // Only refetch organizers when teamDetails changes and we have a valid team ID
    // This prevents infinite loops
    if (teamDetails?.id && loggedIn && !teamDetailsFetching) {
      refetchTeamOrganizers();
      refetchDiskSubscriptionChangeRequests();
    }
  }, [teamDetails?.id, loggedIn]);
  // useEffect(() => {
  //   if(teamDetails?.id && loggedIn && (pathname?.includes('members') || pathname?.includes(`pending-chats`))) {
  //     refetchTeamPendingPosts()
  //   }
  // },[teamDetails?.id, pathname, loggedIn])
  useEffect(() => {
    if(teamDetails?.id && loggedIn && (pathname?.includes('members') || pathname?.includes(`pending-chats`) || activeTab === (t('teamsPage.pendingPosts') || 'Pending Posts'))) {
      refetchTeamPendingPosts()
    }
  },[teamDetails?.id, pathname, loggedIn, activeTab, isOrganizer])

  const diskOrganizer = teamOrganizers?.find?.(
    (o) => o?.user?.id === teamDetails?.disk_payer
  );

  return {
    diskOrganizer,
    createEditDisc,
    leaveTeamDisc,
    reportTeamDisc,
    invitationDisc,
    removeMemberDisc,
    diskStatusDisc,
    makeOrganizerDisc,
    removeOrganizerDisc,
    deleteTeamDisc,
    changeDiskSubscriberDisc,
    diskSubscriptionChangeRequestDisc,
    mode,
    setMode,
    allDegreeConnections,
    allDegreeConnectionsArr,
    slug,
    userProfileInfo,
    isMember,
    isOrganizer,
    isInvited,
    isRequested,
    text,
    setText,
    images,
    setImages,
    videos,
    setVideos,
    gifs,
    setGifs,
    textInputRef,
    addImage,
    removeImage,
    addVideo,
    removeVideo,
    addGif,
    removeGif,
    expanded,
    setExpanded,
    defaultWelcomeNote,

    // chat room
    createTeamChatRoom,
    chatRoom,
    setChatRoom,
    createTeamChatPosts,
    createTeamPostAsyncLoading,

    //pending post for chat
    refetchTeamPendingPosts,
    teamPendingPosts,
    teamPendingPostsFetching,
    approveTeamGroupPost: approveTeamGroupPost_,
    //team
    team,
    setTeam,
    createOrUpdateTeam,
    createOrUpdateTeamLoading: createTeamLoading || updateTeamLoading,

    // members
    teamMembers,
    teamMembersFetching,
    teamMembersLoading,
    refetchTeamMembers,

    // organizers
    teamOrganizers,
    teamOrganizersFetching,
    refetchTeamOrganizers,
    makeTeamOrganizer,
    makeTeamOrganizerLoading: updateTeamMemberStatusLoading,
    removeTeamOrganizer,
    removeTeamOrganizerLoading: updateTeamMemberStatusLoading,

    //requests
    newTeamJoinRequests,
    setNewTeamJoinRequests,
    teamJoinRequests,
    refetchTeamJoinRequests,
    teamJoinRequestsFetching,
    requestToJoinTeam: requestToJoinTeam_,
    requestToJoinTeamLoading,
    approveTeamJoinRequest: approveTeamJoinRequest_,
    approveTeamJoinRequestLoading: updateTeamMemberStatusLoading,
    denyTeamJoinRequest: denyTeamJoinRequest_,
    denyTeamJoinRequestLoading: denyJoinRequestLoading,
    withdrawRequestToJoinTeam: withdrawRequestToJoinTeam_,
    withdrawRequestToJoinTeamLoading: denyJoinRequestLoading,

    //invited
    teamInvitedUsers,
    teamInvitedUsersFetching,
    refetchTeamInvitedUsers,
    withdrawTeamInvite: withdrawTeamInvite_,
    withdrawTeamInviteLoading,
    inviteUsersToTeam: inviteUsersToTeam_,
    inviteUserToTeamLoading,
    acceptInvite,
    acceptInviteLoading: updateTeamMemberStatusLoading,

    //blocked
    teamBlockedMembers,
    teamBlockedMembersFetching,
    refetchTeamBlockedMembers,
    blockTeamMember: blockTeamMember_,
    blockTeamMemberLoading: updateTeamMemberStatusLoading,
    unblockTeamMember: unblockTeamMember_,
    unblockTeamMemberLoading: updateTeamMemberStatusLoading,

    // team details
    teamDetails,
    teamDetailsFetching,
    teamDetailsLoading,
    refetchTeamDetails,

    // listing
    teamsListing,
    teamsListingFetching,
    teamsListingLoading,
    refetchTeamsListing,
    teamsFetchNextPage,
    teamsHasNextPage,
    teamsTotalCount: teamsListingPages?.pages?.length * 10,
    teamsListingPages,
    audienceDisc,
    permissionValue,
    setPermissionValue,
    currentPermissionValue,
    setCurrentPermissionValue,
    permissionId,
    setPermissionId,

    // update team member status
    // : organizer / member / blocked / remove etc
    updateTeamMemberStatus,
    updateTeamMemberStatusLoading,
    removeTeamMember: removeTeamMember_,
    removeTeamMemberLoading,

    // disk status
    updateTeamMemberDiskStatus: updateTeamMemberDiskStatus_,
    updateTeamMemberDiskStatusLoading,

    //leave
    leaveTeam,
    leaveTeamLoading,

    //report
    reportTeam,
    errorExistCompany,
    reportTeamLoading,

    //selected
    selectedUser,
    setSelectedUser,

    selectedTeam,
    setSelectedTeam,

    activeTab,
    setActiveTab,

    isUserOrganizer,
    isUserMember,
    isUserInvited,
    isUserRequested,
    diskAccessStatuses,
    diskStatus,
    setDiskStatus,

    blockTeamMemberDisc,
    currentDiskAccess,
    deleteTeam: deleteTeam_,
    deleteTeamLoading,

    diskSubscriptionChangeRequests,
    diskSubscriptionChangeRequestsFetching,
    diskSubscriptionChangeRequestsLoading,
    refetchDiskSubscriptionChangeRequests,
    changeTeamDiskSubscriber: changeTeamDiskSubscriber_,
    changeTeamDiskSubscriberLoading,
    cancelDiskSubscriptionChangeRequest: cancelDiskSubscriptionChangeRequest_,
    cancelDiskSubscriptionChangeRequestLoading,
    acceptDiskSubscriptionChangeRequest: acceptDiskSubscriptionChangeRequest_,
    acceptDiskSubscriptionChangeRequestLoading,
    myDiskAccessType,

    membersLoaded,
    organizersLoaded,

    teamLoggedInMember,
    teamLoggedInMembersFetching,
    teamLoggedInMembersLoading,
    refetchTeamLoggedInMember,
    handleFileTypes,
    chatTeamSlug,
    setChatTeamSlug,
    searchQuery,
    setSearchQuery,
    filter,
    setFilter,
  };
};
